<script setup>
import { ref, watch, computed } from 'vue'
import axiosIns from '@/plugins/axios'

const props = defineProps({
  userId: {
    type: String,
    default: null,
  },
})

const health = ref(null)
const loading = ref(false)

// Remove-suppression dialog state
const showRemoveDialog = ref(false)
const removalReason = ref('')
const removeBusy = ref(false)
const removeError = ref('')

const suppressionColor = computed(() => {
  if (!health.value) return 'grey'
  if (health.value.isSuppressed) return 'error'
  if (health.value.nearThreshold) return 'warning'
  
  return 'success'
})

const suppressionIcon = computed(() => {
  if (!health.value) return 'mdi-email'
  if (health.value.isSuppressed) return 'mdi-email-off'
  if (health.value.nearThreshold) return 'mdi-email-alert'
  
  return 'mdi-email-check'
})

const suppressionId = computed(() => {
  if (!health.value || !health.value.suppression) return null
  const s = health.value.suppression
  
  return s._id || s.id || null
})

const isHardBounceSuppression = computed(() => {
  if (!health.value || !health.value.suppression) return false
  
  return health.value.suppression.reason === 'hard_bounce'
})

async function fetchHealth() {
  if (!props.userId) return
  loading.value = true
  try {
    const res = await axiosIns.get(`/admin/comms/users/${props.userId}/delivery-health`)
    health.value = res.data.data
  } catch {
    health.value = null
  } finally {
    loading.value = false
  }
}

function openRemoveDialog() {
  removalReason.value = ''
  removeError.value = ''
  showRemoveDialog.value = true
}

async function confirmRemove() {
  removeError.value = ''
  if (!removalReason.value || !removalReason.value.trim()) {
    removeError.value = 'Please provide a reason for removing this suppression.'

    return
  }
  if (!suppressionId.value) {
    removeError.value = 'Suppression id missing — refresh the dialog and try again.'

    return
  }
  removeBusy.value = true
  try {
    await axiosIns.delete(`/admin/comms/suppressions/${suppressionId.value}`, {
      data: { removalReason: removalReason.value.trim() },
    })
    showRemoveDialog.value = false
    await fetchHealth()
  } catch (err) {
    removeError.value = err?.response?.data?.error || err.message || 'Failed to remove suppression.'
  } finally {
    removeBusy.value = false
  }
}

watch(() => props.userId, fetchHealth, { immediate: true })
</script>

<template>
  <div v-if="!loading && health">
    <div class="d-flex align-center flex-wrap">
      <VChip
        :color="suppressionColor"
        :prepend-icon="suppressionIcon"
        size="small"
        class="mr-2"
        label
      >
        <span v-if="health.isSuppressed">Suppressed ({{ health.suppression?.reason?.replace(/_/g, ' ') }})</span>
        <span v-else-if="health.nearThreshold">Near threshold ({{ health.softBounces }}/{{ health.softBounceThreshold }} soft bounces)</span>
        <span v-else>Email healthy</span>
      </VChip>

      <span
        v-if="health.hardBounces > 0"
        class="text-caption text-error mr-2"
      >
        {{ health.hardBounces }} hard bounce{{ health.hardBounces !== 1 ? 's' : '' }} (30d)
      </span>
      <span
        v-if="health.complaints > 0"
        class="text-caption text-deep-orange mr-2"
      >
        {{ health.complaints }} complaint{{ health.complaints !== 1 ? 's' : '' }} (30d)
      </span>

      <VBtn
        v-if="health.isSuppressed && suppressionId"
        size="x-small"
        color="primary"
        variant="text"
        prepend-icon="mdi-email-arrow-right"
        @click="openRemoveDialog"
      >
        Remove suppression
      </VBtn>
    </div>

    <VDialog
      v-model="showRemoveDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Remove suppression</VCardTitle>
        <VCardText>
          <VAlert
            v-if="isHardBounceSuppression"
            type="warning"
            class="mb-3"
            density="compact"
          >
            This email previously hard-bounced. Removing the suppression will allow further sends; reconfirm with the member that the address is valid before proceeding.
          </VAlert>
          <p class="text-body-2 mb-3">
            Removing the suppression for
            <strong>{{ health.suppression?.email }}</strong>
            will resume email delivery to this address.
          </p>
          <VTextarea
            v-model="removalReason"
            label="Reason for removal"
            placeholder="e.g. Member confirmed the email is valid"
            :error-messages="removeError"
            rows="3"
            autofocus
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="showRemoveDialog = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="error"
            :disabled="!removalReason.trim() || removeBusy"
            :loading="removeBusy"
            @click="confirmRemove"
          >
            Remove suppression
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
